import { IRegularPeriod } from '@pepperhq/types-sdk';
import { format, parse } from 'date-fns';

import {
    DAY_TO_DAY_NUMBER,
    DaysOfTheWeek,
    LocationDayOpeningHours,
    LocationOpeningTimes
} from './models/LocationModel';

export function formatTime(value: LocationOpeningTimes): string {
    const date = new Date();
    date.setHours(value.hour, value.minute, 0, 0);
    return format(date, 'h:mm a');
}

// 00:00 is midnight at the end of the day, i.e. 12am the following day, so it is
// overnight. Otherwise flag as overnight when closeTime falls before openTime in
// wall-clock minutes.
export function isOvernight(range: LocationDayOpeningHours): boolean {
    const { openTime, closeTime } = range;
    if (!openTime || !closeTime) {
        return false;
    }
    if (closeTime.hour === 0 && closeTime.minute === 0) {
        return true;
    }
    return closeTime.hour * 60 + closeTime.minute < openTime.hour * 60 + openTime.minute;
}

export interface RangeIssue {
    index: number;
    field: 'from' | 'to';
    message: string;
}

// Cross-range checks for a day/date's splits: they must be entered in chronological
// order and must not overlap. Only the final split may be left open-ended.
// `from`/`to` are zero-padded "HH:mm" strings, so they compare lexically.
// Midnight-crossing ranges (to < from) are skipped for overlap checks since
// the comparison would need to account for the day boundary.
export function getRangeOrderIssues(ranges: { from: string; to: string }[]): RangeIssue[] {
    const issues: RangeIssue[] = [];
    ranges.forEach((current, index) => {
        const isLast = index === ranges.length - 1;
        if (!isLast && current.from && !current.to) {
            issues.push({ index, field: 'to', message: 'Enter a closing time before the next split' });
        }
        if (index === 0 || !current.from) {
            return;
        }
        const previous = ranges[index - 1];
        // 00:00 is midnight at the end of the day (closes next day), so it crosses
        // midnight — as does any range where to < from.
        const prevCrossesMidnight = previous.to && (previous.to === '00:00' || previous.to < previous.from);
        if (prevCrossesMidnight) {
            return;
        }
        if (previous.from && current.from < previous.from) {
            issues.push({ index, field: 'from', message: 'Split hours must be in chronological order' });
        } else if (previous.to && current.from < previous.to) {
            issues.push({ index, field: 'from', message: 'This split overlaps the previous one' });
        }
    });
    return issues;
}

export function formatOverrideDate(dateStr: string): string {
    try {
        return format(parse(dateStr, 'yyyy-MM-dd', new Date()), ' dd/MM/yyyy');
    } catch {
        return dateStr;
    }
}

// Extracts slots for a single day from the flat regular array, stripping the
// day field so the existing display helpers can work with them.
export function getDaySlots(regular: IRegularPeriod[], day: DaysOfTheWeek): LocationDayOpeningHours[] {
    const dayNumber = DAY_TO_DAY_NUMBER[day];
    return regular
        .filter(s => s.openTime.day === dayNumber)
        .map(s => ({
            openTime: { hour: s.openTime.hour, minute: s.openTime.minute },
            closeTime: { hour: s.closeTime.hour, minute: s.closeTime.minute }
        }));
}

export function formatRange(range: LocationDayOpeningHours): string {
    const from = range.openTime ? formatTime(range.openTime) : '';
    const to = range.closeTime ? formatTime(range.closeTime) : '';
    const overnight = isOvernight(range);
    if (from && to) {
        return `${from} - ${to}${overnight ? ' (next day)' : ''}`;
    }
    if (from) {
        return `From ${from}`;
    }
    if (to) {
        return `Until ${to}`;
    }
    return '';
}

// A single open/close range, held as the "HH:mm" strings a native time input
// expects. A date can hold several of these to support split hours.
export interface TimeRangeValue {
    from: string;
    to: string;
}

export interface DateOverrideEntry {
    date: Date | null;
    closed: boolean;
    ranges: TimeRangeValue[];
}

export function createEmptyRange(): TimeRangeValue {
    return { from: '', to: '' };
}

// Converts a stored { hour, minute } into the "HH:mm" value a native time
// input expects.
export function timeToInputValue(value?: LocationOpeningTimes): string {
    if (!value) {
        return '';
    }
    const hours = String(value.hour).padStart(2, '0');
    const minutes = String(value.minute).padStart(2, '0');
    return `${hours}:${minutes}`;
}

// Converts a "HH:mm" time input value into the stored { hour, minute }
// structure.
export function inputValueToTime(value: string): LocationOpeningTimes | undefined {
    if (!value) {
        return undefined;
    }
    const [hours, minutes] = value.split(':');
    const hour = Number(hours);
    const minute = Number(minutes);
    if (Number.isNaN(hour) || Number.isNaN(minute)) {
        return undefined;
    }
    return { hour, minute };
}

// A date with no stored ranges is "closed"; otherwise we keep at least one
// range so the inputs render once the date is re-opened.
export function parseDateEntry(date: Date, ranges: LocationDayOpeningHours[] | undefined): DateOverrideEntry {
    if (!ranges || ranges.length === 0) {
        return { date, closed: true, ranges: [createEmptyRange()] };
    }
    return {
        date,
        closed: false,
        ranges: ranges.map(range => ({
            from: timeToInputValue(range.openTime),
            to: timeToInputValue(range.closeTime)
        }))
    };
}

// Each range needs at least one side filled in (validation guarantees this)
// and may be open-ended.
export function serializeRanges(ranges: TimeRangeValue[]): LocationDayOpeningHours[] {
    return ranges.reduce<LocationDayOpeningHours[]>((acc, range) => {
        const openTime = inputValueToTime(range.from);
        const closeTime = inputValueToTime(range.to);
        if (!openTime && !closeTime) {
            return acc;
        }
        const entry: LocationDayOpeningHours = {};
        if (openTime) {
            entry.openTime = openTime;
        }
        if (closeTime) {
            entry.closeTime = closeTime;
        }
        acc.push(entry);
        return acc;
    }, []);
}
